import { IAAResponse } from 'models/IAA'
import { MParticleConfig } from 'modules/config/state/config-types'
import { URLParams } from 'modules/url-params/state/app-url-params-types'

// Message type constants
const SDK_INIT = 'SDK_INIT'
const DONATION_READY_MSG_FROM_APP = 'DONATION_READY_MSG_FROM_APP'
const ANALYTICS_EVENT_MSG_FROM_APP = 'ANALYTICS_EVENT_MSG_FROM_APP'
const EMBEDDED_APP_VISIBLE = 'EMBEDDED_APP_VISIBLE'
const USER_ID_MSG_TO_APP = 'USER_ID_MSG_TO_APP'
const SESSION_ID_MSG_TO_APP = 'SESSION_ID_MSG_TO_APP'
const IDENTIFY_USER_MESSAGE_FROM_APP = 'IDENTIFY_USER_MESSAGE_FROM_APP'
const NEW_USER_ID_MSG_FROM_APP = 'NEW_USER_ID_MSG_FROM_APP'
const DONATION_CHANGED_MSG_FROM_APP = 'DONATION_CHANGED_MSG_FROM_APP'
const DONATION_COMPLETED_MSG_FROM_APP = 'DONATION_COMPLETED_MSG_FROM_APP'

/**
 * Interface for the initialization message from SDK
 */
export interface InitMsgFromSDK {
  type: typeof SDK_INIT
  payload: {
    params: unknown
    safeguardToken: string
    iaa?: IAAResponse
    mParticleConfig: MParticleConfig
  }
}

/**
 * Sends a donation ready message to the parent frame
 */
export const sendDonationReadyMessage = () => {
  const message = {
    type: DONATION_READY_MSG_FROM_APP,
    payload: {},
  }

  window?.parent.postMessage(message, '*')
}

export const sendDonationChangedMessage = (campaignId: string, params: Partial<URLParams>) => {
  const message = {
    type: DONATION_CHANGED_MSG_FROM_APP,
    payload: { campaignId, params },
  }

  window?.parent.postMessage(message, '*')
}

export const sendDonationCompletedMessage = () => {
  const message = {
    type: DONATION_COMPLETED_MSG_FROM_APP,
    payload: {},
  }

  window?.parent.postMessage(message, '*')
}

/**
 * Sends an analytics event message to the parent frame
 * @param eventName - Name of the analytics event
 * @param eventData - Data associated with the event
 */
export const sendAnalyticsMessage = (eventName: string, eventData: Record<string, any>) => {
  const message = {
    type: ANALYTICS_EVENT_MSG_FROM_APP,
    payload: {
      event: eventName,
      data: eventData,
    },
  }

  window?.parent.postMessage(message, '*')
}

/**
 * Sends an anonymous user ID message to the SDK
 * @param userId - The anonymous user ID
 */
export const sendAnonUserIdMessage = (userId: string) => {
  const message = {
    type: NEW_USER_ID_MSG_FROM_APP,
    payload: { userId },
  }

  window?.parent.postMessage(message, '*')
}

/**
 * Sends an identified user ID message to the SDK
 * @param userId - The identified user ID
 */
export const sendIdentifyUserMessage = (userId: string) => {
  const message = {
    type: IDENTIFY_USER_MESSAGE_FROM_APP,
    payload: { userId },
  }

  window?.parent.postMessage(message, '*')
}

/**
 * Sets up a listener for initialization messages from the parent frame
 * @param callback - Function to be called when an init message is received
 */
export const onInitMessage = (callback: (message: InitMsgFromSDK) => void) => {
  window?.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data.type !== SDK_INIT) {
      return
    }

    callback(event.data)
  })
}

/**
 * Sets up a listener for visibility events from the parent iframe
 * @param callback - Function to be called when the embedded app becomes visible
 */
export const onAppVisibleMessage = (callback: () => void) => {
  window?.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data.type !== EMBEDDED_APP_VISIBLE) {
      return
    }

    callback()
  })
}

/**
 * Sets up a listener for user ID messages from the parent app
 * @param callback - Function to handle the received user ID
 */
export const onUserIdMessage = (callback: (userId: string) => void) => {
  window?.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data.type !== USER_ID_MSG_TO_APP) {
      return
    }

    callback(event.data.payload.userId)
  })
}

/**
 * Sets up a listener for session ID messages from the parent app
 * @param callback - Function to handle the received session ID
 */
export const onSessionIdMessage = (callback: (sessionId: string) => void) => {
  window?.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data.type !== SESSION_ID_MSG_TO_APP) {
      return
    }

    callback(event.data.payload.sessionId)
  })
}
